import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Handshake, Loader2, DollarSign, Coins, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import IntermedioChatDialog from "@/components/IntermedioChatDialog";
import { RubinotCharacterValidator, type RubinotCharacterValidatorHandle } from "@/components/RubinotCharacterValidator";

interface SolicitarIntermedioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anuncioId: string;
  itemNome: string;
  preco: number;
  moeda: string;
  precoCoins?: number | null;
  precoKk?: number | null;
  servidor?: string | null;
  descricao?: string | null;
  anuncianteApelido?: string | null;
  anuncianteAvatarUrl?: string | null;
  itemImageUrl?: string | null;
  sessionContato: string;
  sessionToken: string;
  onSuccess?: () => void;
}

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const SolicitarIntermedioDialog = ({
  open,
  onOpenChange,
  anuncioId,
  itemNome,
  preco,
  moeda,
  precoCoins,
  precoKk,
  servidor,
  descricao,
  anuncianteApelido,
  anuncianteAvatarUrl,
  itemImageUrl,
  sessionContato,
  sessionToken,
  onSuccess,
}: SolicitarIntermedioDialogProps) => {
  const [modalidade, setModalidade] = useState<"dinheiro" | "coins" | "ambos">("dinheiro");
  const [valorNegociado, setValorNegociado] = useState(preco > 0 ? preco.toString() : "");
  const [valorNegociadoCoins, setValorNegociadoCoins] = useState(
    precoCoins != null && precoCoins > 0 ? precoCoins.toString() : ""
  );
  const [pagadorIntermedio, setPagadorIntermedio] = useState("interessado");
  const [personagem, setPersonagem] = useState("");
  const [personagemValidated, setPersonagemValidated] = useState(false);
  const [personagemLastValidated, setPersonagemLastValidated] = useState("");
  const charValidatorRef = useRef<RubinotCharacterValidatorHandle>(null);
  const personagemInputRef = useRef<HTMLInputElement>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rateLimitDialog, setRateLimitDialog] = useState(false);
  const [createdInterId, setCreatedInterId] = useState<string | null>(null);
  const { data: config } = useConfiguracoes();

  const valorIntermedio = parseFloat(config?.valor_intermedio || "10.00");
  const personagemColetor = config?.personagem_coletor || "Rpgcoin";

  const hasPrecoDinheiro = preco > 0;
  const hasPrecoCoins = precoCoins != null && precoCoins > 0;
  const isProposal = !hasPrecoDinheiro && !hasPrecoCoins;
  // Em modo proposta, habilita a modalidade conforme a moeda do anúncio
  const proposalIsCoins = isProposal && moeda === "coins";
  const proposalIsDinheiro = isProposal && moeda !== "coins";
  const hasDinheiro = hasPrecoDinheiro || proposalIsDinheiro;
  const hasCoins = hasPrecoCoins || proposalIsCoins;

  // Auto-select the only available modalidade
  useEffect(() => {
    if (hasDinheiro && !hasCoins) setModalidade("dinheiro");
    else if (!hasDinheiro && hasCoins) setModalidade("coins");
  }, [hasDinheiro, hasCoins]);

  // Reset character name whenever the dialog is opened
  useEffect(() => {
    if (open) {
      setPersonagem("");
      setPersonagemValidated(false);
      setPersonagemLastValidated("");
      setPendingSubmit(false);
    }
  }, [open]);

  // Reset fields when modalidade changes
  useEffect(() => {
    if (modalidade === "dinheiro") {
      setValorNegociado(preco > 0 ? preco.toString() : "");
      setValorNegociadoCoins("");
    } else if (modalidade === "coins") {
      setValorNegociado("");
      setValorNegociadoCoins(precoCoins != null && precoCoins > 0 ? precoCoins.toString() : "");
    } else {
      setValorNegociado(preco > 0 ? preco.toString() : "");
      setValorNegociadoCoins(precoCoins != null && precoCoins > 0 ? precoCoins.toString() : "");
    }
  }, [modalidade]);

  const requireCharValidation = config?.somente_personagem_validado !== "false";

  const submitIntermedio = async (personagemNome: string) => {
    setLoading(true);
    try {
      const bodyData: any = {
        action: "solicitar",
        contato: sessionContato,
        token: sessionToken,
        anuncio_id: anuncioId,
        valor_negociado: modalidade === "coins" ? 0 : parseFloat(valorNegociado),
        pagador_intermedio: pagadorIntermedio,
        modalidade_pagamento: modalidade,
        personagem_interessado: personagemNome,
      };
      if (modalidade === "coins" || modalidade === "ambos") {
        bodyData.valor_negociado_coins = parseInt(valorNegociadoCoins);
      }

      const { data, error } = await supabase.functions.invoke("intermediacao", {
        body: bodyData,
      });

      if (error || data?.error) {
        if (data?.code === "RATE_LIMIT" || data?.error?.includes("limite")) {
          setRateLimitDialog(true);
          onOpenChange(false);
        } else {
          toast({ title: data?.error || "Erro ao solicitar intermédio", variant: "destructive" });
        }
        return;
      }

      toast({ title: "Intermédio solicitado com sucesso!", description: "O anunciante será notificado." });
      const newInterId = data?.intermediacao?.id || null;
      onOpenChange(false);
      onSuccess?.();
      if (newInterId) setCreatedInterId(newInterId);
    } catch (e) {
      console.error(e);
      toast({ title: "Erro inesperado", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (modalidade === "dinheiro" || modalidade === "ambos") {
      const valor = parseFloat(valorNegociado);
      if (isNaN(valor) || valor <= 0) {
        toast({ title: "Informe um valor válido em R$", variant: "destructive" });
        return;
      }
    }
    if (modalidade === "coins" || modalidade === "ambos") {
      const valorC = parseInt(valorNegociadoCoins);
      if (isNaN(valorC) || valorC <= 0) {
        toast({ title: "Informe uma quantidade válida de coins", variant: "destructive" });
        return;
      }
    }
    const personagemTrim = personagem.trim();
    if (!personagemTrim) {
      toast({ title: "Informe o nome do personagem envolvido na transação", variant: "destructive" });
      personagemInputRef.current?.focus();
      return;
    }

    if (!personagemValidated || personagemLastValidated !== personagemTrim) {
      setPendingSubmit(true);
      await charValidatorRef.current?.validate(personagemTrim);
      return;
    }

    await submitIntermedio(personagemTrim);
  };

  const handleCharConfirmed = (info: { name: string; world: string }) => {
    setPersonagem(info.name);
    setPersonagemLastValidated(info.name);
    setPersonagemValidated(true);
    if (pendingSubmit) {
      setPendingSubmit(false);
      submitIntermedio(info.name);
    }
  };

  const handleCharProceed = (name: string) => {
    setPersonagemLastValidated(name);
    setPersonagemValidated(true);
    if (pendingSubmit) {
      setPendingSubmit(false);
      submitIntermedio(name);
    }
  };

  const handleCharCorrect = () => {
    setPendingSubmit(false);
    setPersonagemValidated(false);
    setTimeout(() => personagemInputRef.current?.focus(), 100);
  };

  const formatPrice = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  return (
    <>

    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-pixel text-sm">
            <Handshake className="w-5 h-5 text-primary" />
            Solicitar Intermédio
          </DialogTitle>
          <DialogDescription>
            Solicite a intermediação segura da negociação pela plataforma.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item info unificado */}
          <div className="p-3 rounded-lg border border-border/50 bg-secondary/30 min-w-0 flex gap-3">
            <img
              src={itemImageUrl || getItemImageUrl(itemNome)}
              alt={itemNome}
              className="w-12 h-12 object-contain flex-shrink-0 rounded bg-background/40 p-1"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                const proxy = getItemImageUrl(itemNome);
                if (img.src !== proxy && !img.dataset.fallback) {
                  img.dataset.fallback = "1";
                  img.src = proxy;
                } else {
                  img.src = "/placeholder.svg";
                }
              }}
            />
            <div className="min-w-0 flex-1 flex flex-col">
              <p className="font-pixel text-xs text-primary break-words">
                {itemNome}
                {servidor && (
                  <span className="ml-2 font-sans font-normal text-[11px] text-muted-foreground">
                    🌐 {servidor}
                  </span>
                )}
              </p>
              {descricao && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap [overflow-wrap:anywhere] break-words">{descricao}</p>}
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                {preco > 0 && <span>R$ {formatPrice(preco)}</span>}
                {precoCoins != null && precoCoins > 0 && <span>{precoCoins.toLocaleString("pt-BR")} RC</span>}
                {precoKk != null && precoKk > 0 && <span>{precoKk.toLocaleString("pt-BR")} kk</span>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 self-end truncate max-w-full">
                por <span className="font-medium text-foreground">{anuncianteApelido || "Anunciante"}</span>
              </p>
            </div>
          </div>

          {/* Modalidade de pagamento */}
          <div>
            <Label className="text-sm">Modalidade do pagamento</Label>
            <RadioGroup value={modalidade} onValueChange={(v) => setModalidade(v as any)} className="mt-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="dinheiro" id="mod-dinheiro" disabled={!hasDinheiro} />
                <Label htmlFor="mod-dinheiro" className={`text-sm flex items-center gap-1 ${!hasDinheiro ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <DollarSign className="w-3.5 h-3.5" /> Dinheiro (R$)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="coins" id="mod-coins" disabled={!hasCoins} />
                <Label htmlFor="mod-coins" className={`text-sm flex items-center gap-1 ${!hasCoins ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <Coins className="w-3.5 h-3.5" /> Coins (RC)
                </Label>
              </div>
            </RadioGroup>
            <p className="text-[10px] text-muted-foreground mt-2">
              💡 Para combinar Dinheiro + Coins, use o botão "Propor novo valor" no chat após iniciar o intermédio.
            </p>
          </div>

          {/* Valores negociados */}
          {(modalidade === "dinheiro" || modalidade === "coins" || modalidade === "ambos") && (
            <div className="grid gap-3 grid-cols-1">
              {(modalidade === "dinheiro" || modalidade === "ambos") && (
                <div>
                  <Label htmlFor="valor-reais" className="text-sm">Valor (R$)</Label>
                  {isProposal ? (
                    <Input
                      id="valor-reais"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={valorNegociado}
                      onChange={(e) => setValorNegociado(e.target.value)}
                      placeholder="Informe sua proposta"
                      className="mt-1"
                    />
                  ) : (
                    <Input
                      id="valor-reais"
                      type="text"
                      value={valorNegociado ? `R$ ${parseFloat(valorNegociado).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"}
                      readOnly
                      disabled
                      className="mt-1 bg-muted/40 cursor-not-allowed"
                    />
                  )}
                </div>
              )}
              {(modalidade === "coins" || modalidade === "ambos") && (
                <div>
                  <Label htmlFor="valor-coins" className="text-sm">Coins (RC)</Label>
                  {isProposal ? (
                    <Input
                      id="valor-coins"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      value={valorNegociadoCoins}
                      onChange={(e) => setValorNegociadoCoins(e.target.value.replace(/\D/g, ""))}
                      placeholder="Informe sua proposta em RC"
                      className="mt-1"
                    />
                  ) : (
                    <Input
                      id="valor-coins"
                      type="text"
                      value={valorNegociadoCoins ? `${parseInt(valorNegociadoCoins).toLocaleString("pt-BR")} RC` : "—"}
                      readOnly
                      disabled
                      className="mt-1 bg-muted/40 cursor-not-allowed"
                    />
                  )}
                </div>
              )}
              {(modalidade === "coins" || modalidade === "ambos") && (
                <p className="text-[10px] text-muted-foreground">
                  As coins deverão ser transferidas para o personagem <strong className="text-primary">{personagemColetor}</strong> durante o processo.
                </p>
              )}
            </div>
          )}

          {/* Pagador do intermédio */}
          <div>
            <Label className="text-sm">Quem paga o intermédio? (R$ {formatPrice(valorIntermedio)})</Label>
            <RadioGroup value={pagadorIntermedio} onValueChange={setPagadorIntermedio} className="mt-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="interessado" id="pag-eu" />
                <Label htmlFor="pag-eu" className="text-sm cursor-pointer">Eu (interessado)</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="anunciante" id="pag-anunciante" />
                <Label htmlFor="pag-anunciante" className="text-sm cursor-pointer">Anunciante</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="dividido" id="pag-dividido" />
                <Label htmlFor="pag-dividido" className="text-sm cursor-pointer">
                  Dividido (R$ {formatPrice(valorIntermedio / 2)} cada)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Personagem envolvido */}
          <div>
            <Label htmlFor="personagem-interessado" className="text-sm">
              Personagem envolvido na transação *
            </Label>
            <div className="relative mt-1">
              <Input
                id="personagem-interessado"
                ref={personagemInputRef}
                value={personagem}
                onChange={(e) => {
                  setPersonagem(e.target.value);
                  if (personagemValidated && e.target.value.trim() !== personagemLastValidated) {
                    setPersonagemValidated(false);
                  }
                }}
                placeholder="Nome exato do personagem no RubinOT"
                maxLength={60}
                className={!personagem.trim() ? "border-destructive/50" : ""}
              />
              {personagemValidated && personagem.trim() === personagemLastValidated && (
                <CheckCircle2 className="w-4 h-4 text-green-500 absolute right-2 top-1/2 -translate-y-1/2" />
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Será validado no RubinOT antes de prosseguir.
            </p>
          </div>

          <Button onClick={handleSubmit} disabled={loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Handshake className="w-4 h-4 mr-2" />}
            Confirmar Solicitação
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      <AlertDialog open={rateLimitDialog} onOpenChange={setRateLimitDialog}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-pixel text-sm">
              <Clock className="w-5 h-5 text-orange-400" />
              Limite atingido
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Você atingiu o limite de <strong>10 solicitações de intermédio por hora</strong>. 
              Aguarde alguns minutos antes de tentar novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="font-pixel text-xs">Entendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {createdInterId && (
        <IntermedioChatDialog
          open={!!createdInterId}
          onOpenChange={(o) => { if (!o) setCreatedInterId(null); }}
          intermediacaoId={createdInterId}
          sessionContato={sessionContato}
          sessionToken={sessionToken}
          onStatusChange={onSuccess}
        />
      )}

      <RubinotCharacterValidator
        ref={charValidatorRef}
        requireValidation={requireCharValidation}
        onConfirm={handleCharConfirmed}
        onProceedUnvalidated={handleCharProceed}
        onCorrect={handleCharCorrect}
      />
    </>
  );
};

export default SolicitarIntermedioDialog;
